import React, { ReactNode, useEffect } from "react";
import "./Modal.css";
import { Button } from "../Button";
import { Close } from "@carbon/icons-react";

export interface ModalProps {
  /**
   * Título del modal
   */
  title?: string;

  /**
   * Contenido principal del modal
   */
  children?: ReactNode;

  /**
   * Componente o contenido adicional para el slot
   */
  slotComponent?: ReactNode;

  /**
   * Si el modal está abierto
   */
  isOpen?: boolean;

  /**
   * Callback al cerrar el modal
   */
  onClose?: () => void;

  /**
   * Variante del modal
   */
  variant?: "basic" | "full-screen" | "icon";

  /**
   * Icono para variante icon
   */
  icon?: ReactNode;

  /**
   * Texto para el botón primario
   */
  primaryButtonText?: string;

  /**
   * Callback al hacer clic en el botón primario
   */
  onPrimaryClick?: () => void;

  /**
   * Texto para el botón secundario
   */
  secondaryButtonText?: string;

  /**
   * Callback al hacer clic en el botón secundario
   */
  onSecondaryClick?: () => void;

  /**
   * Texto para el botón terciario
   */
  tertiaryButtonText?: string;

  /**
   * Callback al hacer clic en el botón terciario
   */
  onTertiaryClick?: () => void;

  /**
   * Disposición de los botones
   */
  buttonLayout?: "row" | "column";

  /**
   * Si el modal es para mobile
   */
  isMobile?: boolean;

  /**
   * Clase CSS adicional
   */
  className?: string;
}

/**
 * Componente Modal para mostrar contenido en una capa superpuesta
 */
export const Modal: React.FC<ModalProps> = ({
  title = "Title",
  children,
  slotComponent,
  isOpen = false,
  onClose,
  variant = "basic",
  icon,
  primaryButtonText,
  onPrimaryClick,
  secondaryButtonText,
  onSecondaryClick,
  tertiaryButtonText,
  onTertiaryClick,
  buttonLayout = "row",
  isMobile = false,
  className = "",
}) => {
  // Bloquear el scroll del cuerpo cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const containerClasses = [
    "qida-modal-container",
    `qida-modal-container--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const modalClasses = [
    "qida-modal",
    `qida-modal--${variant}`,
    isMobile ? "qida-modal--mobile" : "",
    buttonLayout === "column" ? "qida-modal--column-buttons" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const footerClasses = [
    "qida-modal__footer",
    `qida-modal__footer--${buttonLayout}`,
    isMobile ? "qida-modal__footer--mobile" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Definir tamaños de botón según la variante
  const buttonSize = variant === "full-screen" ? "lg" : "md";

  return (
    <div className={containerClasses}>
      <div className={modalClasses} aria-modal="true" role="dialog">
        <header className="qida-modal__header">
          {variant === "icon" ? (
            <div className="qida-modal__header-title-container">
              <div className="qida-modal__title">{title}</div>
              <button
                className="qida-modal__close-button"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <Close size={24} />
              </button>
            </div>
          ) : (
            <>
              <div className="qida-modal__title">{title}</div>
              <button
                className="qida-modal__close-button"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <Close size={24} />
              </button>
            </>
          )}
          {variant === "icon" && icon && (
            <div className="qida-modal__icon">{icon}</div>
          )}
        </header>

        <div className="qida-modal__content">
          {children && <div className="qida-modal__text">{children}</div>}
          {slotComponent && (
            <div className="qida-modal__slot">{slotComponent}</div>
          )}
        </div>

        {(primaryButtonText || secondaryButtonText || tertiaryButtonText) && (
          <footer className={footerClasses}>
            {variant === "full-screen" && buttonLayout === "column" ? (
              // Layout especial para botones en columna en full-screen
              <>
                {primaryButtonText && (
                  <Button
                    onClick={onPrimaryClick}
                    variant="primary"
                    className="qida-modal__primary-button"
                    size={buttonSize}
                    fullWidth
                  >
                    {primaryButtonText}
                  </Button>
                )}
                {secondaryButtonText && (
                  <Button
                    onClick={onSecondaryClick}
                    variant="secondary"
                    className="qida-modal__secondary-button"
                    size={buttonSize}
                    fullWidth
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {tertiaryButtonText && (
                  <Button
                    onClick={onTertiaryClick}
                    variant="tertiary"
                    className="qida-modal__tertiary-button"
                    size={buttonSize}
                    fullWidth
                  >
                    {tertiaryButtonText}
                  </Button>
                )}
              </>
            ) : isMobile && buttonLayout === "column" ? (
              // Layout para botones en columna en mobile
              <>
                {primaryButtonText && (
                  <Button
                    onClick={onPrimaryClick}
                    variant="primary"
                    className="qida-modal__primary-button"
                    size="md"
                    fullWidth
                  >
                    {primaryButtonText}
                  </Button>
                )}
                {secondaryButtonText && (
                  <Button
                    onClick={onSecondaryClick}
                    variant="secondary"
                    className="qida-modal__secondary-button"
                    size="md"
                    fullWidth
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {tertiaryButtonText && (
                  <Button
                    onClick={onTertiaryClick}
                    variant="tertiary"
                    className="qida-modal__tertiary-button"
                    size="md"
                    fullWidth
                  >
                    {tertiaryButtonText}
                  </Button>
                )}
              </>
            ) : isMobile && buttonLayout === "row" ? (
              // Layout para botones en fila en mobile (solo dos botones - primario y secundario)
              <>
                {secondaryButtonText && (
                  <Button
                    onClick={onSecondaryClick}
                    variant="secondary"
                    className="qida-modal__secondary-button"
                    size="md"
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {primaryButtonText && (
                  <Button
                    onClick={onPrimaryClick}
                    variant="primary"
                    className="qida-modal__primary-button"
                    size="md"
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </>
            ) : (
              // Layout normal para desktop
              <>
                {buttonLayout === "row" && !isMobile && tertiaryButtonText && (
                  <Button
                    onClick={onTertiaryClick}
                    variant="tertiary"
                    className="qida-modal__tertiary-button"
                    size={buttonSize}
                  >
                    {tertiaryButtonText}
                  </Button>
                )}
                <div
                  className={
                    buttonLayout === "row" && !isMobile
                      ? "qida-modal__right-buttons"
                      : ""
                  }
                >
                  {buttonLayout === "column" && tertiaryButtonText && (
                    <Button
                      onClick={onTertiaryClick}
                      variant="tertiary"
                      className="qida-modal__tertiary-button"
                      size={buttonSize}
                    >
                      {tertiaryButtonText}
                    </Button>
                  )}
                  {secondaryButtonText && (
                    <Button
                      onClick={onSecondaryClick}
                      variant="secondary"
                      className="qida-modal__secondary-button"
                      size={buttonSize}
                    >
                      {secondaryButtonText}
                    </Button>
                  )}
                  {primaryButtonText && (
                    <Button
                      onClick={onPrimaryClick}
                      variant="primary"
                      className="qida-modal__primary-button"
                      size={buttonSize}
                    >
                      {primaryButtonText}
                    </Button>
                  )}
                </div>
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
};

export default Modal;
